import { useEffect, useRef, useState } from "react";
import { alpha, Box, Card, CardActionArea, Typography } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { useThemeContext } from "../context/themeContext";
import SectionHeader from "./SectionHeader";
import MotionInView from "./MotionInView";
import { useScaleGestures } from "../hooks/useScaleGestures";

// Threshold for the carousel's own inView check below — how much of it
// must be on-screen before its items' entrance animation fires.
const VIEWPORT_AMOUNT = 0.2;

// Cap the stagger so items far down a long list don't lag noticeably behind
// the ones already visible on load.
const MAX_STAGGER_INDEX = 6;
const STAGGER_STEP = 0.04;

// The row's bottom padding (pb: 2), left free so hover/tap scale and the
// entrance animation can bleed into it; the edge fade stops above it.
const ROW_BOTTOM_PADDING = 16;
// Gap above the row when there's no description line under the title (the
// description brings its own).
const ROW_TOP_GAP = 8;
// Height of the renderCaption line under each item (4px gap + a 20px line),
// fixed so the "See all" card can stop short of it and match the tiles above
const CAPTION_HEIGHT = 24;

// The "See all" card's surface — exported so a tile beside it (the profile
// standouts' add tile) can match
export const seeAllCardBg = (theme) => alpha(theme.palette.secondary.main, 0.5);

/**
 * Trailing "See all" card: a real link to href (with router state) — so
 * crawlers can follow it and a modified click opens a new tab — or a plain
 * button calling onClick. Touch devices get no native long-press link menu. `disabled`: greyed out with no destination yet. `captioned`: the
 * row's items have captions, so it stops short of them — the row stretches it
 * to the full item height, caption included.
 */
function SeeAllCard({
  href,
  state,
  onClick,
  disabled = false,
  captioned = false,
}) {
  const scaleGestures = useScaleGestures();
  const { isTouchDevice } = useThemeContext();
  const isLink = !onClick && !!href && !disabled;
  return (
    <Card
      component={motion.div}
      {...(!disabled && scaleGestures)}
      elevation={1}
      sx={{
        flexShrink: 0,
        scrollSnapAlign: "start",
        // Match the Explore cards' surface exactly (same bgcolor/elevation/
        // overlay suppression) so this card doesn't stand out as a different shade.
        bgcolor: seeAllCardBg,
        backgroundImage: "none",
        display: "flex",
        flexDirection: "column",
        mb: captioned ? `${CAPTION_HEIGHT}px` : 0,
        // This is the last card in the row and, once the bleed is disabled at
        // "lg"+, sits flush against the scroll container's right edge with no
        // reserved space — anchor growth to that edge (mirrors the first item's
        // anchor in ItemScale) so hover/tap scale doesn't get clipped there.
        transformOrigin: "100% 50%",
      }}
    >
      <CardActionArea
        disabled={disabled}
        {...(isLink
          ? {
              component: RouterLink,
              to: href,
              state,
              onContextMenu: isTouchDevice
                ? (e) => e.preventDefault()
                : undefined,
            }
          : { onClick })}
        sx={{
          ...(isLink && isTouchDevice && { WebkitTouchCallout: "none" }),
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          // The card itself already has a hover/tap scale; suppress MUI's default
          // darkening overlay so that's the only hover cue (matches CoverCollection.js).
          "&:hover .MuiCardActionArea-focusHighlight": { opacity: 0 },
        }}
      >
        <Typography
          variant="button"
          color={disabled ? "text.disabled" : "primary"}
          sx={{ textTransform: "none" }}
        >
          See all
        </Typography>
      </CardActionArea>
    </Card>
  );
}

// One item's hover/tap scale wrapper (see Carousel's itemScale)
function ItemScale({ mode, isFirst, children }) {
  const full = useScaleGestures();
  const hover = useScaleGestures({ tap: false });
  if (mode === "none") return children;
  return (
    <motion.div
      {...(mode === "hover" ? hover : full)}
      // The first item sits flush against the scroll container's left edge
      // (once the bleed is off at "lg"+) — grow rightward so it isn't clipped.
      // Mirrors the "See all" card's right-edge anchor.
      style={isFirst ? { originX: 0 } : undefined}
    >
      {children}
    </motion.div>
  );
}

/**
 * A titled, horizontally scrolling row — Explore's activity and collection
 * rows, and the profile's standouts and collections. Bleeds past the page's
 * gutters on both edges; the first item snaps back to the content margin so it
 * aligns with the heading.
 *
 * Items:
 * - items + renderItem(item, index): what each slot shows
 * - itemScale: each item's hover/tap scale (useScaleGestures), applied here so
 *   every row behaves the same — "full" (hover + tap press-in, the default),
 *   "hover" (no press-in: for tiles with PosterTile's long press, whose pop a
 *   press-in would mute) or "none"; or itemScale(item) for rows whose items
 *   differ. The first item's scale is anchored to its left edge so it never
 *   grows past the row's clip boundary.
 * - getItemKey(item, index): React key (defaults to the index)
 * - itemSx: sizing for each item's slot (e.g. responsive widths), or
 *   itemSx(item) for rows whose items differ (e.g. a divider among posters)
 * - renderCaption(item, index): optional one-line caption under an item (e.g.
 *   "3 weeks ago"). A null caption still keeps the line, so tiles stay aligned
 * - emptyState: shown in place of the row when there are no items
 *
 * Header (see SectionHeader):
 * - title, titleIcon (pass with color="primary"), info (popover content)
 * - description: a line under the title
 *
 * "See all":
 * - seeMoreLink (+ seeMoreState router state) or onSeeMore handler
 * - seeMoreIcon: show the header's "See all" as this icon (primary icon button)
 * - showSeeMoreCard: whether the trailing "See all" card renders (default true)
 * - seeMoreDisabled: show "See all" (header and card) greyed out, for a
 *   destination that isn't built yet — needs no link or handler
 */
export default function Carousel({
  title,
  titleIcon,
  info,
  description,
  items,
  renderItem,
  getItemKey = (item, index) => index,
  itemSx,
  itemScale = "full",
  renderCaption,
  emptyState,
  seeMoreLink,
  seeMoreState,
  onSeeMore,
  seeMoreIcon,
  showSeeMoreCard = true,
  seeMoreDisabled = false,
}) {
  const hasSeeMore = !!seeMoreLink || !!onSeeMore || seeMoreDisabled;
  // Checked once on the whole carousel (not on each item) so the entrance
  // animation is driven by it scrolling into view vertically — scrolling the
  // row sideways afterwards doesn't move it, so it never re-fires. Watches the
  // root rather than the row: useInView only attaches on mount, so a row that
  // appears later (after the empty state, say) would never be observed and
  // its items would stay invisible.
  const rootRef = useRef(null);
  const inView = useInView(rootRef, { once: true, amount: VIEWPORT_AMOUNT });
  const rowRef = useRef(null);
  // Whether the row has more to scroll to on the right — the edge fade below
  // only shows then, so it never sits over a fully revealed last item.
  const [hasMoreRight, setHasMoreRight] = useState(false);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return; // empty state: no row rendered
    // 1px tolerance: scroll positions can be fractional at some zoom levels
    const update = () =>
      setHasMoreRight(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
    update();
    row.addEventListener("scroll", update, { passive: true });
    // Width changes (resize, breakpoint) change how much overflows
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      observer.disconnect();
    };
    // items.length: a different number of items changes the scroll width
  }, [items.length]);

  return (
    <Box ref={rootRef}>
      <SectionHeader
        title={title}
        icon={titleIcon}
        info={info}
        buttonLabel={hasSeeMore ? "See all" : undefined}
        buttonHref={seeMoreLink}
        buttonState={seeMoreState}
        onButtonClick={onSeeMore}
        buttonDisabled={seeMoreDisabled}
        buttonIcon={seeMoreIcon}
      />
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          mb={0.75}
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
          }}
        >
          {description}
        </Typography>
      )}

      {items.length === 0 && emptyState ? (
        emptyState
      ) : (
        // The no-description gap is a margin on this wrapper, not the row: the
        // row's own margin would collapse through it, leaving the wrapper's top
        // (and so the edge fade's) misaligned with the row
        <Box
          sx={{
            position: "relative",
            mt: description ? 0 : `${ROW_TOP_GAP}px`,
          }}
        >
          <Box
            ref={rowRef}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1.5,
              overflowX: "auto",
              // Explicitly hidden (not the default "visible", which the browser would
              // silently force to behave like "auto" as long as overflow-x isn't
              // visible) — this row only scrolls horizontally, never vertically.
              // The extra vertical padding below gives hover/tap scale and the
              // entrance animation room to bleed into without being clipped by it.
              overflowY: "hidden",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              pt: 0.5,
              pb: `${ROW_BOTTOM_PADDING}px`,
              // Bleed past the page's ambient gutter so the row scrolls edge-to-edge —
              // but only while the page itself is fluid. Once the outer shell hits its
              // "lg" max-width cap the page has its own visible margins either side, so
              // bleeding here would stop short of the viewport edge and look broken (see
              // FiveStarMovies.js for the same fix). Disable the bleed at that point.
              mx: { xs: -2, sm: -3, lg: 0 },
              pr: { xs: 2, sm: 3, lg: 0 },
              "& > :first-of-type": { ml: { xs: 2, sm: 3, lg: 0 } },
              scrollPaddingLeft: { xs: "16px", sm: "24px", lg: 0 },
            }}
          >
            {items.map((item, index) => (
              // The Box is the flex item so responsive itemSx widths apply to it. It's
              // also every item's snap point: the row snaps mandatorily, so items
              // without one would leave the See all card as the only target and the
              // row would keep snapping to the end.
              <Box
                key={getItemKey(item, index)}
                sx={{
                  flexShrink: 0,
                  scrollSnapAlign: "start",
                  ...(typeof itemSx === "function" ? itemSx(item) : itemSx),
                }}
              >
                <MotionInView
                  delay={Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP}
                  inView={inView}
                >
                  <ItemScale
                    mode={
                      typeof itemScale === "function"
                        ? itemScale(item)
                        : itemScale
                    }
                    isFirst={index === 0}
                  >
                    {renderItem(item, index)}
                  </ItemScale>
                  {renderCaption && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      component="p"
                      sx={{
                        mt: "4px",
                        height: `${CAPTION_HEIGHT - 4}px`,
                        lineHeight: `${CAPTION_HEIGHT - 4}px`,
                      }}
                    >
                      {renderCaption(item, index) ?? "\u00a0"}
                    </Typography>
                  )}
                </MotionInView>
              </Box>
            ))}
            {hasSeeMore && showSeeMoreCard && (
              <SeeAllCard
                href={seeMoreLink}
                state={seeMoreState}
                onClick={onSeeMore}
                disabled={seeMoreDisabled}
                captioned={!!renderCaption}
              />
            )}
          </Box>

          {/* Once the bleed above is disabled at "lg"+, the row ends flush against the
              container edge instead of scrolling off past it — fade the cut-off item
              into the page background so the cutoff reads as intentional, not abrupt.
              Only while there's more to scroll: at the end, or when nothing
              overflows, there's no cutoff to soften. Stops above the row's bottom
              padding so it doesn't fade empty space. z-index clears any z-indexed
              content inside an item (e.g. CoverCollection.js's 5-poster stack). */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", lg: "block" },
              opacity: hasMoreRight ? 1 : 0,
              transition: "opacity 150ms ease-out",
              position: "absolute",
              top: 0,
              bottom: ROW_BOTTOM_PADDING,
              right: 0,
              width: 56,
              pointerEvents: "none",
              zIndex: 10,
              background: (theme) =>
                `linear-gradient(to right, transparent, ${theme.palette.background.default})`,
            }}
          />
        </Box>
      )}
    </Box>
  );
}
